import React, { useState, useEffect } from 'react'
import { Typography, IconButton } from '@mui/material'
import ShareIcon from '@mui/icons-material/Share'
import { Activity, ActivityPageFieldVisibility, Client } from '../../../pure-js/types/GrooverTypes'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'
import Box from './Box'
import GrooverText from './GrooverText'
import { getGrooverText } from '../libs/GrooverTextRepository'
import useAppState from '../hooks/useAppState'

interface RegistrationLocationProps {
  customer: Client
  activity: Activity
  visibility: ActivityPageFieldVisibility
}

const RegistrationLocation: React.FC<RegistrationLocationProps> = ({ customer, activity, visibility }) => {
  const { showLocation, showRoom, showGoogleLinkAndMap } = visibility
  const { state } = useAppState()
  const language = state.user?.language
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  const [mapError, setMapError] = useState<boolean>(false)
  const [mapLoaded, setMapLoaded] = useState<boolean>(false)

  const mapContainerStyle = {
    width: '100%',
    height: '400px'
  }

  const location = customer.locations.find((loc) => loc.id === activity.locationId)

  // Detect when Google Maps API is loaded
  useEffect(() => {
    if (!googleMapsApiKey || !location?.center || mapError) return

    // Check if Google Maps is already loaded
    if (typeof window !== 'undefined' && (window as any).google && (window as any).google.maps) {
      setMapLoaded(true)
      return
    }

    // Poll for Google Maps to load (with timeout)
    const checkInterval = setInterval(() => {
      if (typeof window !== 'undefined' && (window as any).google && (window as any).google.maps) {
        setMapLoaded(true)
        clearInterval(checkInterval)
      }
    }, 100)

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval)
      if (!mapLoaded) {
        console.warn('Google Maps failed to load within timeout')
        setMapError(true)
      }
    }, 10000)

    return () => {
      clearInterval(checkInterval)
      clearTimeout(timeout)
    }
  }, [googleMapsApiKey, location?.center, mapError, mapLoaded])

  const showHeader = showLocation || showRoom || showGoogleLinkAndMap

  return (
    <div className="segment-container">
      {showHeader && (
        <Box
          direction="row"
          gap="6px"
          align="flex-start"
          justify="space-between"
          fullWidth
          bottom
          changeDirectionOnMobile
        >
          <Typography variant="h5" component="h2" className="header-title">
            <GrooverText textKey="externalActivityPageSegmentPlaceHeader" language={language} />
          </Typography>
          {showGoogleLinkAndMap && location?.googleUrl ? (
            <IconButton
              className="share-button"
              aria-label={getGrooverText('userWeb.activityDetail.location.openInGoogleMapsAria', language)}
              onClick={() => {
                location?.googleUrl && window.open(location.googleUrl, '_blank')
              }}
            >
              <ShareIcon />
              <Typography className="share-button-text">
                <GrooverText
                  textKey="userWeb.activityDetail.location.openInGoogleMaps"
                  language={language}
                  variant="body.regular"
                  style={{ color: 'inherit' }}
                />
              </Typography>
            </IconButton>
          ) : null}
        </Box>
      )}
      {(showLocation || showRoom) && (
        <Box direction="column" gap="6px" fullWidth bottom>
          {showLocation && (
            <Typography variant="body1">
              <strong>{location?.name}</strong>
              <br />
              {location?.adress}
            </Typography>
          )}
          {showRoom && (
            <Box direction="column" gap="6px" align="center">
              <Typography variant="body1">
                <strong>
                  <GrooverText textKey="externalActivityPageRoomPlace" language={language} />
                </strong>{' '}
                <br />
                {activity.place}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showGoogleLinkAndMap && googleMapsApiKey && location?.center && !mapError && (
        <LoadScript googleMapsApiKey={googleMapsApiKey}>
          {mapLoaded && (
            <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
              <GoogleMap mapContainerStyle={mapContainerStyle} center={location.center} zoom={17}>
                <Marker position={location.center} />
              </GoogleMap>
            </div>
          )}
        </LoadScript>
      )}
    </div>
  )
}

export default RegistrationLocation
